
import { defineComponent, PropType, Ref, watch } from 'vue'
import { ElForm } from 'element-plus'
import JrButton from '@/components/jr-parts/JrButton.vue'
import { startLoading, endLoading } from '@/plugins/loading'
import GeneralAttachedFiles from '@/components/inputs/GeneralAttachedFiles.vue'
import JrFileInput from '@/components/jr-parts/JrFileInput.vue'

import { ProximityCreate, ProximityUpdate } from '@/requests/proximity'
import { RequestHeaderProximityDetailEditForm } from '@/types/forms'
import { JrProximity } from '@/api'
import { ConstructionTypes } from '@/constants/enums'
import { proximityEditRule } from '@/constants/rules'
import { MESSAGE_TYPE_CONFIRM, addMessageQueue } from '@/plugins/messageQueue'
import Message from '@/services/message'
import { ErrorResponse } from '@/requests/baseRequest'

export default defineComponent({
  name: 'ProximityDetailRegist',
  emits: [
    'update:errorFoundationDrawingFile',
    'update:errorWorkingRangeFile',
    'returnRoute',
    'update:isValid',
    'update:notSaved',
  ],
  components: {
    JrButton,
  },
  props: {
    // 近接工事ID
    proximityId: {
      type: Number,
      required: false,
      default: null,
    },
    formRef: {
      type: Object as PropType<InstanceType<typeof ElForm>>,
      required: false,
      default: null,
    },
    form: {
      type: Object as PropType<RequestHeaderProximityDetailEditForm>,
      required: false,
      default: null,
    },
    fileRefs: {
      type: Array as PropType<Array<Ref<InstanceType<typeof JrFileInput>>>>,
      required: false,
      default: null,
    },
    generalAttachedFileRefs: {
      type: Object as PropType<InstanceType<typeof GeneralAttachedFiles>>,
      required: false,
      default: null,
    },
    // 保存処理実行フラグ(trueで実行する)
    isSave: {
      type: Boolean,
      required: false,
      default: false,
    },
    isShowConfirmDialog: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  setup(props, context) {
    const confirm = () => {
      context.emit('update:isValid', true)
    }

    watch(
      () => props.isSave,
      () => {
        if (props.isSave) {
          //代理申請時は、変更内容のダイアログではなく、保存確認のダイアログを表示
          if (!props.isShowConfirmDialog) {
            // 登録処理
            addMessageQueue(
              MESSAGE_TYPE_CONFIRM,
              Message.codes.CONFIRM_SAVE,
              async () => await save(),
              () => {
                // Cancel時の処理
                context.emit('update:notSaved')
              }
            )
          } else {
            // 編集処理
            save()
          }
        }
      }
    )
    // 保存処理
    const save = async () => {
      const loadingKey = 'proximity detail edit'
      startLoading(loadingKey)
      const cancelLoadingCallback = () => {
        endLoading(loadingKey)
      }

      // ファイルアップロード
      for (const ref of props.fileRefs) {
        if (ref.value) {
          await ref.value.uploadFile(cancelLoadingCallback)
        }
      }
      // その他ファイルアップロード
      if (props.generalAttachedFileRefs != null) {
        await props.generalAttachedFileRefs.allUpload(cancelLoadingCallback)
      }

      // API呼び出し
      if (props.proximityId) {
        const updateRequest = new ProximityUpdate(props.proximityId, setParams())
        updateRequest.setErrorCallback(cancelLoadingCallback)
        await updateRequest.exec()
        endLoading(loadingKey)
        requestProcess(updateRequest)
      } else {
        const createRequest = new ProximityCreate(setParams())
        createRequest.setErrorCallback(cancelLoadingCallback)
        await createRequest.exec()
        endLoading(loadingKey)
        requestProcess(createRequest)
      }
    }

    // リクエスト送信処理
    function requestProcess(request: ProximityUpdate | ProximityCreate): void {
      if (request.success) {
        context.emit('returnRoute')
        return
      }
      const errorResponse: ErrorResponse = request.getErrorResponse()
      if (errorResponse) {
        const responseData = errorResponse.getErrorResponseData()
        // 保存エラー時に親に失敗を伝える
        context.emit('update:notSaved', responseData)
      }
    }

    // 保存リクエストパラメータ設定
    const setParams = (): JrProximity => {
      return {
        request_header: {
          general_attached_file: props.form.general_attached_file ? props.form.general_attached_file : [],
          department_code: props.form.department_code as string,
          department_name: props.form.department_name as string,
          department: props.form.department as number,
          applicant_title: props.form.applicant_title as string,
          meter: props.form.meter as number,
          applicant_business_name: props.form.applicant_business_name as string,
          applicant_manager_name: props.form.applicant_manager_name as string,
          applicant_address: props.form.applicant_address as string,
          applicant_tel: props.form.applicant_tel as string,
          applicant_email: props.form.applicant_email ? (props.form.applicant_email as string) : null,
        },
        history_comment: props.form.history_comment as string,
        construction_address: props.form.proximity.construction_address as string,
        construction_building_name: props.form.proximity.construction_building_name as string,
        line: props.form.proximity.line as string,
        line_name: props.form.proximity.line_name as string,
        start_station: props.form.proximity.start_station as number,
        start_station_name: props.form.proximity.start_station_name as string,
        end_station: props.form.proximity.end_station as number,
        end_station_name: props.form.proximity.end_station_name as string,
        degree: props.form.proximity.degree as string,
        construction_latitude: props.form.proximity.construction_latitude as string,
        construction_longitude: props.form.proximity.construction_longitude as string,
        construction_detail: props.form.proximity.construction_detail as string,
        construction_type: props.form.proximity.construction_type as number,
        construction_type_other:
          props.form.proximity.construction_type === ConstructionTypes.OTHER
            ? (props.form.proximity.construction_type_other as string)
            : null,
        construction_purpose: props.form.proximity.construction_purpose as string,
        is_use_heavy: props.form.proximity.is_use_heavy as boolean,
        use_heavy_remark: props.form.proximity.use_heavy_remark
          ? (props.form.proximity.use_heavy_remark as string)
          : null,
        is_foundation_work: props.form.proximity.is_foundation_work as boolean,
        foundation_work_remark: props.form.proximity.foundation_work_remark
          ? (props.form.proximity.foundation_work_remark as string)
          : null,
        is_ground_completion: props.form.proximity.is_ground_completion as boolean,
        is_use_out_land: props.form.proximity.is_use_out_land as boolean,
        is_without_protective: props.form.proximity.is_without_protective as boolean,
        without_protective_remark: props.form.proximity.without_protective_remark
          ? (props.form.proximity.without_protective_remark as string)
          : null,
        start_plan_date: props.form.proximity.start_plan_date as string,
        end_plan_date: props.form.proximity.end_plan_date as string,
        location_map_file:
          props.form.proximity.location_map_file && props.form.proximity.location_map_file !== -1
            ? (props.form.proximity.location_map_file as number)
            : null,
        ground_plan_file:
          props.form.proximity.ground_plan_file && props.form.proximity.ground_plan_file !== -1
            ? (props.form.proximity.ground_plan_file as number)
            : null,
        cross_section_file:
          props.form.proximity.cross_section_file && props.form.proximity.cross_section_file !== -1
            ? (props.form.proximity.cross_section_file as number)
            : null,
        actual_place_photo_file:
          props.form.proximity.actual_place_photo_file && props.form.proximity.actual_place_photo_file !== -1
            ? (props.form.proximity.actual_place_photo_file as number)
            : null,
        foundation_drawing_file:
          props.form.proximity.foundation_drawing_file && props.form.proximity.foundation_drawing_file !== -1
            ? (props.form.proximity.foundation_drawing_file as number)
            : null,
        plan_temporary_work_file:
          props.form.proximity.plan_temporary_work_file && props.form.proximity.plan_temporary_work_file !== -1
            ? (props.form.proximity.plan_temporary_work_file as number)
            : null,
        working_range_file:
          props.form.proximity.working_range_file && props.form.proximity.working_range_file !== -1
            ? (props.form.proximity.working_range_file as number)
            : null,
        cadastral_map_file:
          props.form.proximity.cadastral_map_file && props.form.proximity.cadastral_map_file !== -1
            ? (props.form.proximity.cadastral_map_file as number)
            : null,
        survey_drawing_of_acreage_file:
          props.form.proximity.survey_drawing_of_acreage_file &&
          props.form.proximity.survey_drawing_of_acreage_file !== -1
            ? (props.form.proximity.survey_drawing_of_acreage_file as number)
            : null,
      }
    }
    return {
      confirm,
      save,
      setParams,
      rules: proximityEditRule,
    }
  },
})
